import { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  useMediaQuery,
  MenuItem,
  Fab,
} from "@mui/material";

import { useInView } from "react-intersection-observer";
import { useDebounce } from "use-debounce";
import { useTheme } from "@emotion/react";
import { Add } from "@mui/icons-material";
import useSalesTickets from "../../../hooks/useSalesTickets";
import { AuthContext } from "../../../context/AuthContext";
import TextFieldDebounced from "../../../components/TextFieldDebounced";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import { currencyFormat } from "../../../components/NewDetail/calculations/utils";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const SalesTickets = ({ search, status }) => {
  console.log(search, status);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { authToken, ezorder } = useContext(AuthContext);
  const [
    salesTickets,
    onRefresh,
    refreshing,
    onLoadMore,
    loadingMore,
    total,
    hasMore,
  ] = useSalesTickets(authToken, 50, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  // Update URL
  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate || statusUpdate) {
      if (searchUpdate) {
        query.push(`search=${searchUpdate}`);
      }
      if (statusUpdate) {
        query.push(`status=${statusUpdate}`);
      }
    }
    if (query.length > 0) {
      navigate(`/consumables-pos?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/consumables-pos`);
    }
  };

  useEffect(() => {
    if (salesTickets.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <Typography variant="h4" component="div">
        Consumables Sales Tickets
      </Typography>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          sx={{ width: "200px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"PENDING_PAYMENT"}>Pending Payment</MenuItem>
          <MenuItem value={"PAID"}>Paid</MenuItem>
          <MenuItem value={"CANCELLED"}>Cancelled</MenuItem>
          <MenuItem value={"ALL"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        TableContainerComponent={Paper}
        TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: "200px" }}>
                Status
              </TableCell>
              <TableCell align="left">ID</TableCell>
              <TableCell align="left">Customer</TableCell>
              <TableCell align="left">Date Created</TableCell>
              <TableCell align="left">Created By</TableCell>
              <TableCell align="left">$</TableCell>
            </TableRow>
          </TableHead>
        )}
        items={salesTickets}
        renderRow={(item, index) => {
          return (
            <TableRow
              key={item.id}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  background: "#eeeeee",
                },
              }}
              onClick={() => {
                navigate(`/consumables-pos/${item.id}`);
              }}
            >
              <TableCell align="left">
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: "25px" }}
                  color={
                    item.status == "PENDING_PAYMENT"
                      ? "warning" // "Draft"
                      : //   : item.status == "ESTIMATE_APPROVAL_NEEDED"
                      //   ? "info" // "Sent"
                      item.status == "CANCELLED"
                      ? "error" // "Cancelled"
                      : // All Other Status is "Sold"
                        "success" //"Sold"
                  }
                >
                  {item.status}
                </Button>
              </TableCell>
              <TableCell align="left">{item.id}</TableCell>
              <TableCell align="left">
                {item.isNotAnIndustryCustomer ? "" : item.company.name}
              </TableCell>
              <TableCell align="left">
                {dayjs(item.createdAt).tz("America/Phoenix").format("MM/DD/YY")}
              </TableCell>
              <TableCell align="left">{item.createdBy.fullName}</TableCell>
              <TableCell align="left">
                {currencyFormat(item.totalPrice ?? 0)}
              </TableCell>
            </TableRow>
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Fab
          onClick={() => {
            navigate("/consumables-pos/new-sales-ticket");
          }}
          variant="extended"
          color="success"
        >
          <Add /> Sales Ticket
        </Fab>
      </div>
    </Box>
  );
};
export default SalesTickets;
